import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import KibliLogo from "../components/KibliLogo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "padre" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const newUser = await register(form);
      const role = newUser?.role || form.role;
      if (role === "profesional") {
        navigate("/profile/create");
      } else {
        navigate("/home");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md flex flex-col items-center gap-6 animate-fade-in-up">
        <KibliLogo size={64} />
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-extrabold">Crear cuenta</h1>
          <p className="text-slate-500 mt-1">Únete a KibliApp en segundos</p>
        </div>

        <form onSubmit={onSubmit} className="w-full space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Nombre</label>
            <input
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Tu nombre"
              data-testid="register-name-input"
              className="input-base"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="tu@email.com"
              data-testid="register-email-input"
              className="input-base"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Contraseña</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                placeholder="Mínimo 6 caracteres"
                data-testid="register-password-input"
                className="input-base pr-12"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="toggle-register-password-visibility"
                className="absolute inset-y-0 right-3 my-auto h-9 w-9 flex items-center justify-center text-slate-500 hover:text-slate-700"
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">¿Quién eres?</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => update("role", "padre")}
                data-testid="role-padre-btn"
                className={`flex flex-col items-center justify-center text-center gap-2 px-3 py-5 rounded-2xl border-2 transition ${form.role === "padre" ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <i className={`fa-solid fa-user-group text-2xl ${form.role === "padre" ? "text-blue-600" : "text-slate-400"}`}></i>
                <span className={`font-bold leading-tight ${form.role === "padre" ? "text-blue-700" : "text-slate-700"}`}>
                  Busco canguro<br />o profe
                </span>
                <span className="text-xs text-slate-500">Para mi familia</span>
              </button>
              <button
                type="button"
                onClick={() => update("role", "profesional")}
                data-testid="role-prof-btn"
                className={`flex flex-col items-center justify-center text-center gap-2 px-3 py-5 rounded-2xl border-2 transition ${form.role === "profesional" ? "border-teal-500 bg-teal-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <i className={`fa-solid fa-graduation-cap text-2xl ${form.role === "profesional" ? "text-teal-600" : "text-slate-400"}`}></i>
                <span className={`font-bold leading-tight ${form.role === "profesional" ? "text-teal-700" : "text-slate-700"}`}>
                  Soy canguro<br />o profe
                </span>
                <span className="text-xs text-slate-500">Ofrezco mis servicios</span>
              </button>
            </div>
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="register-error">{error}</div>}

          <button type="submit" disabled={loading} data-testid="register-submit-btn" className="btn-primary w-full py-4">
            {loading ? "Creando cuenta..." : "Crear cuenta"}
          </button>
        </form>

        <Link to="/login" className="text-blue-600 font-semibold" data-testid="goto-login-link">
          ¿Ya tienes cuenta? Iniciar sesión
        </Link>
      </div>
    </div>
  );
}
